/**
 * 遥操作连接管理器。信令服务只负责协商；业务数据由浏览器直连球端。
 * 信令与视频完全独立；浏览器是 Offerer，机器人是 Answerer。
 */
import { connectWithUrl } from '@/utils/websocket';
import { generateRandomId, generateUUID, importPrivateKey, signData } from '@/utils/signUtils';
import { initProtocol, encode, decode } from '@/utils/teleopProtocol';

const CHANNEL_OPTIONS = {
  joystick: { ordered: false, maxRetransmits: 0 },
  command: { ordered: true },
  telemetry: { ordered: false, maxRetransmits: 1 }
};
// 图 1 定义：throttle 量程 [0, 1000]，中位/停止值 500；其他轴量程 [-1000, 1000]，中位 0。
const NEUTRAL = { yaw: 0, throttle: 500, roll: 0, pitch: 0, buttons: 0 };
const DEFAULT_SPEED_MODE = 2; // SPEED_MODE_MEDIUM
const ACTIVE_JOYSTICK_INTERVAL = 40; // 25Hz, 0.04s
const STABLE_JOYSTICK_INTERVAL = 100; // 10Hz, 0.1s
const JOYSTICK_ACTIVITY_WINDOW = 250;
const COMMAND_RESPONSE_TIMEOUT = 5000;
const COMMAND_RESPONSE_TYPES = {
  'teleop.set_enabled': 'SetTeleopEnabledResponse',
  'teleop.set_speed_mode': 'SetSpeedModeResponse'
};

function toUint8Array(data) {
  if (data instanceof Uint8Array) return data;
  if (data instanceof ArrayBuffer) return new Uint8Array(data);
  return data;
}

export default class TeleoperationConnection {
  // 保存一条遥操作会话的信令、RTC、DataChannel 和命令状态。
  constructor(options = {}) {
    this.robotId = options.robotId;
    this.robotCode = options.robotCode;
    this.iceServers = options.iceServers || [];
    this.onStateChange = options.onStateChange || (() => {});
    this.onCommandResponse = options.onCommandResponse || (() => {});
    this.onError = options.onError || (() => {});
    this.onLog = options.onLog || (() => {});
    this.signalingSocket = null;
    this.signalingReady = false;
    this.signalingKey = null;
    this.pc = null;
    this.offerTimeout = null;
    this.channels = { joystick: null, command: null, telemetry: null };
    this.state = 'idle';
    this.cryptoPrivateKey = null;
    this.teleopEnabled = false;
    this.lastJoystickSentAt = 0;
    this.lastJoystickChangedAt = 0;
    this.currentJoystick = { ...NEUTRAL };
    this.lastJoystickAt = 0;
    this.joystickMotionLogged = false;
    this.watchdogTimer = null;
    this.sequence = 0;
    this.sessionId = 0;
    this.connectResolve = null;
    this.connectReject = null;
    // 主动关闭时忽略 WebSocket/RTC 的异步回调，避免把 idle 覆盖成 error。
    this.isCleaningUp = false;
    this.speedModeInitialized = false;
    this.disconnectPromise = null;
    this.pendingCommands = new Map();
  }

  // 输出普通连接日志并同步到界面。
  log(message, data) {
    const time = new Date().toLocaleTimeString('zh-CN', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
    if (data === undefined) console.log('[Teleop] ' + message);
    else console.log('[Teleop] ' + message, data);
    this.onLog(time + ' ' + message);
  }

  // 输出错误日志并同步到界面。
  logError(message, data) {
    console.error('[Teleop] ' + message, data || '');
    this.onLog('[ERROR] ' + message);
  }

  // 更新连接状态并通知 UI。
  setState(state) {
    if (this.state === state) return;
    this.state = state;
    this.onStateChange(state);
  }

  // 导入私钥并开始新一轮信令协商。
  async connect(privateKeyPem) {
    if (!this.robotCode) throw new Error('机器人工号为空');
    if (this.disconnectPromise) await this.disconnectPromise.catch(() => {});
    if (this.pc || this.signalingSocket || this.cryptoPrivateKey) {
      await this.disconnect('idle');
    }
    const sessionId = ++this.sessionId;
    this.rejectPendingConnection(new Error('连接已取消'));
    this.cleanupResources();
    this.isCleaningUp = false;
    this.speedModeInitialized = false;
    this.currentJoystick = { ...NEUTRAL };
    this.lastJoystickSentAt = 0;
    this.lastJoystickChangedAt = Date.now();
    if (privateKeyPem) this.cryptoPrivateKey = await importPrivateKey(privateKeyPem);
    if (sessionId !== this.sessionId) throw new Error('连接已取消');
    if (!this.cryptoPrivateKey) throw new Error('私钥未加载');
    initProtocol();
    this.setState('signalling');
    return new Promise((resolve, reject) => {
      this.connectResolve = resolve;
      this.connectReject = reject;
      this.initSignaling(sessionId);
    });
  }

  // 创建独立的遥操作信令 WebSocket。
  initSignaling(sessionId) {
    this.closeSignaling();
    const clientId = generateRandomId(10);
    this.signalingKey = 'WebRTCTeleopSignaling_' + this.robotId + '_' + clientId;
    connectWithUrl(
      '/signaling/' + clientId,
      this.signalingKey,
      null,
      async (data) => {
        if (sessionId !== this.sessionId) return;
        try { await this.handleSignalingMessage(JSON.parse(data), sessionId); }
        catch (error) { this.failConnection('处理信令消息失败', error, sessionId); }
      },
      (socket) => {
        if (sessionId !== this.sessionId) {
          socket.close();
          return;
        }
        this.signalingSocket = socket;
        this.signalingReady = true;
        this.setState('connecting');
        this.sendOfferRequest(sessionId);
      },
      (error) => {
        this.failConnection('信令连接失败', error, sessionId);
      },
      false,
      (event, socket) => {
        if (sessionId !== this.sessionId || socket !== this.signalingSocket) return;
        this.signalingSocket = null;
        this.signalingReady = false;
        if (this.isCleaningUp) return;
        // WebRTC 连接成功后，WebSocket 信令连接可以正常关闭（协商完成）
        // 只有在连接未成功时，WebSocket 关闭才视为异常
        if (this.state === 'connected') {
          this.log('信令连接已关闭（WebRTC 连接成功，信令协商完成）');
        } else {
          // 遥操作采用单客户端控制权；会话关闭通常意味着被新客户端替换。
          // 必须同时停止 DataChannel，避免旧页面继续发送控制量。
          this.failConnection('遥操作信令会话已关闭，控制权可能已转移', event, sessionId);
        }
      }
    );
  }

  // 创建三个 DataChannel、生成 Offer，并发送完整 SDP。
  async sendOfferRequest(sessionId) {
    if (!this.signalingSocket || !this.signalingReady) return;
    try {
      this.closePeerConnection();
      const config = { bundlePolicy: 'max-bundle', iceCandidatePoolSize: 0 };
      if (this.iceServers.length) config.iceServers = this.iceServers;
      const pc = new RTCPeerConnection(config);
      this.pc = pc;
      this.setupPeerConnectionListeners(pc, sessionId);
      Object.keys(CHANNEL_OPTIONS).forEach((label) => {
        this.setupChannel(pc.createDataChannel(label, CHANNEL_OPTIONS[label]), sessionId);
      });
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await this.waitForIceGathering(pc);
      if (sessionId !== this.sessionId || pc !== this.pc || !this.signalingSocket) return;
      // 信令服务约定：遥操作通过 robotCode + _teleop 路由到数据通道进程；type offer。
      this.signalingSocket.send(JSON.stringify({ id: this.robotCode + '_teleop', type: 'offer', sdp: pc.localDescription.sdp }));
      this.log('Offer 已发送，等待 Answer');
      this.clearOfferTimeout();
      this.offerTimeout = setTimeout(() => {
        if (this.state === 'connecting') {
          this.failConnection('等待设备响应超时（15s）', null, sessionId);
        }
      }, 15000);
    } catch (error) {
      this.failConnection('创建 Offer 失败', error, sessionId);
    }
  }

  // 处理信令服务返回的 Answer、Candidate 或错误消息。
  async handleSignalingMessage(message, sessionId) {
    if (sessionId !== this.sessionId) return;
    if (message.type === 'teleop_replaced') {
      const reason = '设备遥控权已转移给其他客户端，当前连接已断开';
      this.logError(reason);
      this.onError(reason);
      const rejection = new Error(reason);
      rejection.teleopReported = true;
      this.rejectPendingConnection(rejection);
      await this.disconnect('disconnected', { sendStop: false });
    } else if (message.type === 'answer') {
      if (!this.pc) throw new Error('收到 Answer 时 PeerConnection 不存在');
      await this.pc.setRemoteDescription(new RTCSessionDescription(message));
      this.log('设置 Answer 完成');
    } else if (message.type === 'candidate') {
      if (!this.pc) throw new Error('收到 Candidate 时 PeerConnection 不存在');
      try {
        // 球端发送的 candidate 字符串可能缺少 sdpMid/sdpMLineIndex
        // 由于使用 bundle 策略且只有一个 datachannel，sdpMLineIndex 默认为 0
        await this.pc.addIceCandidate({ 
          candidate: message.candidate,
          sdpMid: null,
          sdpMLineIndex: 0
        });
        this.log('添加 Candidate 完成');
      } catch (error) {
        // SDP 中已包含完整 candidate，trickle ICE 失败不影响连接
        this.log('Candidate 添加失败（SDP 中已包含，忽略）');
      }
    } else if (message.type === 'offer') {
      this.logError('收到机器人 Offer；当前协议要求浏览器作为 Offerer');
    } else if (message.type === 'error') {
      this.failConnection('信令错误: ' + (message.message || '未知错误'), null, sessionId);
    }
  }

  // 等待本地 ICE candidate 收集完成后再发送 SDP。
  waitForIceGathering(pc) {
    return new Promise((resolve) => {
      if (pc.iceGatheringState === 'complete') return resolve();
      const timer = setTimeout(() => {
        pc.removeEventListener('icegatheringstatechange', handler);
        resolve();
      }, 10000);
      const handler = () => {
        if (pc.iceGatheringState === 'complete') {
          clearTimeout(timer);
          pc.removeEventListener('icegatheringstatechange', handler);
          resolve();
        }
      };
      pc.addEventListener('icegatheringstatechange', handler);
    });
  }

  // 监听 ICE/PeerConnection 状态并在异常时触发清理。
  setupPeerConnectionListeners(pc, sessionId) {
    pc.addEventListener('iceconnectionstatechange', () => {
      if (sessionId !== this.sessionId || pc !== this.pc) return;
      const state = pc.iceConnectionState;
      this.log('ICE 状态: ' + state);
      if (state === 'failed') {
        this.failConnection('ICE 连接失败', null, sessionId);
      } else if (state === 'disconnected') {
        // 球端以 1.5s 无 joystick 数据作为失联保护；浏览器停止本地发送即可。
        this.setJoystickEnabled(false);
        setTimeout(() => {
          if (sessionId === this.sessionId && pc === this.pc && pc.iceConnectionState === 'disconnected') {
            this.failConnection('ICE 连接已断开', null, sessionId);
          }
        }, 5000);
      }
    });
    pc.addEventListener('connectionstatechange', () => {
      if (sessionId === this.sessionId && pc === this.pc && pc.connectionState === 'failed') {
        this.failConnection('PeerConnection 连接失败', null, sessionId);
      }
    });
  }

  // 配置 joystick、command、telemetry DataChannel 的收发和生命周期。
  setupChannel(channel, sessionId) {
    if (!CHANNEL_OPTIONS[channel.label]) return;
    this.channels[channel.label] = channel;
    channel.binaryType = 'arraybuffer';
    channel.onopen = () => {
      if (sessionId !== this.sessionId || this.channels[channel.label] !== channel) return;
      this.log('channel [' + channel.label + '] 已打开');
      // joystick 和 command 都打开后才算遥操作连接成功；telemetry 只是预留通道，不阻塞可遥控状态。
      if (this.isChannelOpen('joystick') && this.isChannelOpen('command')) {
        this.clearOfferTimeout();
        if (this.state !== 'connected') {
          this.setState('connected');
          this.startWatchdog();
          this.resolvePendingConnection();
          this.initializeDefaultSpeedMode();
        }
      }
    };
    channel.onclose = () => {
      if (sessionId !== this.sessionId || this.channels[channel.label] !== channel) return;
      this.channels[channel.label] = null;
      if (!this.isChannelOpen('joystick') || !this.isChannelOpen('command')) {
        this.setState('disconnected');
        this.stopWatchdog();
        this.rejectPendingConnection(new Error(channel.label + ' 通道已关闭'));
        // 通道意外关闭时，若 command 仍可用，先尽力发送正式停止命令再清理。
        this.disconnect('disconnected');
      }
    };
    channel.onerror = (error) => this.logError('channel [' + channel.label + '] 错误', error);
    if (channel.label === 'command') channel.onmessage = (event) => this.handleCommandMessage(event.data);
    if (channel.label === 'telemetry') channel.onmessage = (event) => this.onLog('telemetry ' + (event.data.byteLength || 0) + ' bytes');
  }

  // 按活动状态以 25Hz/5Hz 持续发送最近一帧摇杆值。
  startWatchdog() {
    this.stopWatchdog();
    this.lastJoystickAt = Date.now();
    this.lastJoystickChangedAt = Date.now();
    this.scheduleJoystickSend(0);
  }

  // 每帧发送完成后再按当前状态安排下一帧，避免轮询频率叠加造成发送漂移。
  scheduleJoystickSend(delay) {
    if (!this.teleopEnabled || !this.isChannelOpen('joystick')) return;
    this.watchdogTimer = setTimeout(() => {
      this.watchdogTimer = null;
      if (!this.teleopEnabled || !this.isChannelOpen('joystick')) return;
      const sent = this.sendJoystickNow(this.currentJoystick);
      if (!sent) return;
      const interval = Date.now() - this.lastJoystickChangedAt < JOYSTICK_ACTIVITY_WINDOW
        ? ACTIVE_JOYSTICK_INTERVAL
        : STABLE_JOYSTICK_INTERVAL;
      this.scheduleJoystickSend(interval);
    }, delay);
  }

  // 停止本地摇杆定时发送。
  stopWatchdog() {
    if (this.watchdogTimer) clearTimeout(this.watchdogTimer);
    this.watchdogTimer = null;
  }

  // 开关 joystick 发送状态，并在启用时恢复中位值和定时器。
  setJoystickEnabled(enabled) {
    this.teleopEnabled = !!enabled;
    if (this.teleopEnabled) {
      this.currentJoystick = { ...NEUTRAL, ...this.currentJoystick };
      this.lastJoystickChangedAt = Date.now();
      this.lastJoystickSentAt = 0;
      if (this.isChannelOpen('joystick') && !this.watchdogTimer) this.startWatchdog();
    } else {
      this.stopWatchdog();
      this.currentJoystick = { ...NEUTRAL };
      this.joystickMotionLogged = false;
    }
  }

  // 接收 UI 的摇杆值，做限幅并按频率节流后发送。
  sendJoystick(data) {
    // 每次从当前整帧输入重建轴状态，不继承旧帧值，避免无关轴残留。
    this.currentJoystick = this.normalizeJoystick(data);
    this.lastJoystickChangedAt = Date.now();
    if (!this.teleopEnabled || !this.isChannelOpen('joystick')) return false;
    const isMoving = this.currentJoystick.yaw !== NEUTRAL.yaw
      || this.currentJoystick.throttle !== NEUTRAL.throttle
      || this.currentJoystick.roll !== NEUTRAL.roll
      || this.currentJoystick.pitch !== NEUTRAL.pitch
      || this.currentJoystick.buttons !== NEUTRAL.buttons;
    if (isMoving && !this.joystickMotionLogged) {
      this.log('joystick 控制量开始发送: yaw=' + this.currentJoystick.yaw
        + ', throttle=' + this.currentJoystick.throttle
        + ', roll=' + this.currentJoystick.roll
        + ', pitch=' + this.currentJoystick.pitch);
      this.joystickMotionLogged = true;
    } else if (!isMoving && this.joystickMotionLogged) {
      this.log('joystick 控制量已回中');
      this.joystickMotionLogged = false;
    }
    return true;
  }

  // 编码并发送一帧裸 ManualControl protobuf。
  sendJoystickNow(data = NEUTRAL) {
    const channel = this.channels.joystick;
    if (!channel || channel.readyState !== 'open') return false;
    try {
      this.currentJoystick = this.normalizeJoystick(data);
      const sequence = this.sequence++;
      const encoded = encode('ManualControl', {
        header: {
          sequence,
          request_timestamp_ms: Date.now(),
          request_id: generateUUID()
        },
        yaw: this.currentJoystick.yaw,
        throttle: this.currentJoystick.throttle,
        roll: this.currentJoystick.roll,
        pitch: this.currentJoystick.pitch,
        buttons: this.currentJoystick.buttons
      });
      const previousSentAt = this.lastJoystickSentAt;
      channel.send(encoded);
      this.lastJoystickSentAt = Date.now();
      this.lastJoystickAt = this.lastJoystickSentAt;
      const interval = previousSentAt ? this.lastJoystickSentAt - previousSentAt : 0;
      this.log('joystick 帧已发送: interval=' + interval + 'ms'
        + ', yaw=' + this.currentJoystick.yaw
        + ', throttle=' + this.currentJoystick.throttle
        + ', roll=' + this.currentJoystick.roll
        + ', pitch=' + this.currentJoystick.pitch);
      return true;
    } catch (error) {
      this.logError('发送 joystick 失败', error);
      return false;
    }
  }

  // 判断指定 DataChannel 当前是否处于 open 状态。
  isChannelOpen(label) {
    return !!this.channels[label] && this.channels[label].readyState === 'open';
  }

  // 将四轴和按钮值限制到协议量程。
  normalizeJoystick(data = NEUTRAL) {
    const finite = (value, fallback) => Number.isFinite(Number(value)) ? Number(value) : fallback;
    const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
    return {
      yaw: clamp(finite(data.yaw, NEUTRAL.yaw), -1000, 1000),
      throttle: clamp(finite(data.throttle, NEUTRAL.throttle), 0, 1000),
      roll: clamp(finite(data.roll, NEUTRAL.roll), -1000, 1000),
      pitch: clamp(finite(data.pitch, NEUTRAL.pitch), -1000, 1000),
      buttons: Math.max(0, Math.min(0xffffffff, Math.floor(finite(data.buttons, NEUTRAL.buttons))))
    };
  }

  // 建立连接后主动下发默认中速。
  initializeDefaultSpeedMode() {
    if (this.speedModeInitialized || !this.isChannelOpen('command') || !this.cryptoPrivateKey) return;
    this.speedModeInitialized = true;
    // 默认中速只有收到成功回执后才视为初始化完成。
    this.setSpeedMode(DEFAULT_SPEED_MODE).catch((error) => {
      this.speedModeInitialized = false;
      this.logError('默认中速模式设置失败', error);
      this.onError('默认中速模式设置失败: ' + (error.message || String(error)));
    });
  }

  // 签名并封装 command Envelope，可等待球端回执。
  async sendCommand(method, payloadType, payload, options = {}) {
    if (!this.isChannelOpen('command')) throw new Error('command 通道未就绪');
    if (!this.cryptoPrivateKey) throw new Error('私钥未加载');
    const payloadBytes = encode(payloadType, payload);
    // 浏览器直接完成 RSA-SHA1；signData 按 Java RSAUtil2.sign 输出 Base64 ASCII 字节。
    const signature = await signData(this.cryptoPrivateKey, payloadBytes);
    console.log('[Teleop] sendCommand 被调用, method:', method, 'payloadType:', payloadType, 'payload:', payload);
    const messageId = generateUUID();
    const requestId = payload && payload.header && payload.header.request_id;
    const envelopeBytes = encode('Envelope', {
      header: {
        type: 1,
        device_id: String(this.robotCode),
        method,
        message_id: messageId,
        timestamp_ms: Date.now(),
        correlation_id: ''
      },
      signed: { payload: payloadBytes, signature }
    });
    const awaitResponse = options.awaitResponse !== false;
    let responsePromise = null;
    if (awaitResponse && requestId && COMMAND_RESPONSE_TYPES[method]) {
      responsePromise = new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
          this.pendingCommands.delete(requestId);
          reject(new Error('等待 command 回执超时: ' + method));
        }, COMMAND_RESPONSE_TIMEOUT);
        this.pendingCommands.set(requestId, { resolve, reject, timer, method, messageId, requestId });
      });
    }
    try {
      this.channels.command.send(envelopeBytes);
    } catch (error) {
      if (requestId && this.pendingCommands.has(requestId)) {
        const pending = this.pendingCommands.get(requestId);
        clearTimeout(pending.timer);
        this.pendingCommands.delete(requestId);
        pending.reject(error);
      }
      throw error;
    }
    this.log('command 已发送: ' + method);
    if (responsePromise) return responsePromise;
    return envelopeBytes;
  }

  // 发送遥控使能/停止命令，并同步本地 joystick 状态。
  async setTeleopEnabled(enabled) {
    if (!enabled) this.setJoystickEnabled(false);
    try {
      const result = await this.sendCommand('teleop.set_enabled', 'SetTeleopEnabledRequest', {
        header: { sequence: this.sequence++, request_timestamp_ms: Date.now(), request_id: generateUUID() },
        enabled: !!enabled
      });
      if (enabled) this.setJoystickEnabled(true);
      return result;
    } catch (error) {
      // 停止命令发送失败时也必须停止本地数据发送，交由球端 1.5s 保护兜底。
      if (!enabled) this.setJoystickEnabled(false);
      throw error;
    }
  }

  // 发送低速、中速或高速模式命令。
  async setSpeedMode(mode, options = {}) {
    return this.sendCommand('teleop.set_speed_mode', 'SetSpeedModeRequest', {
      header: { sequence: this.sequence++, request_timestamp_ms: Date.now(), request_id: generateUUID() },
      mode
    }, options);
  }

  // 解码 command 上行 Envelope，并完成待处理请求的回执匹配。
  async handleCommandMessage(data) {
    try {
      if (typeof data === 'string') {
        this.log('收到 command 文本上行: ' + data.slice(0, 200));
        this.onCommandResponse(data);
        return;
      }
      const bytes = data instanceof Blob ? new Uint8Array(await data.arrayBuffer()) : toUint8Array(data);
      this.log('收到 command 二进制上行: ' + (bytes && bytes.byteLength || 0) + ' bytes');
      let envelope = null;
      let response = null;
      let method = '';
      // 优先按协议约定解析 Envelope.raw。
      try {
        envelope = decode('Envelope', bytes);
        method = envelope && envelope.header && envelope.header.method || '';
      } catch (error) {
        this.log('上行不是 Envelope，尝试按裸响应解析');
      }
      if (envelope) {
        const header = envelope.header || {};
        const rawLength = envelope.raw && envelope.raw.length || 0;
        const signedLength = envelope.signed && envelope.signed.payload && envelope.signed.payload.length || 0;
        this.log('上行 Envelope: method=' + (header.method || '(empty)')
          + ', raw=' + rawLength + ' bytes, signedPayload=' + signedLength + ' bytes');
      }
      const pending = this.pendingCommands.values().next().value;
      const expectedMethod = pending && pending.method || '(none)';
      const expectedRequestId = pending && pending.requestId || '(none)';
      const responseType = COMMAND_RESPONSE_TYPES[method];
      if (!envelope) {
        this.log('回执不匹配: expected Envelope，actual=裸二进制，expectedMethod=' + expectedMethod);
        this.onCommandResponse(null, null);
        return;
      }
      const methodCompatible = pending && method === pending.method;
      if (pending && !methodCompatible) {
        this.log('回执不匹配: method，expected=' + pending.method + ', actual=' + (method || '(empty)'));
        this.onCommandResponse(envelope, null);
        return;
      }
      if (pending && method !== pending.method) {
        this.log('临时兼容回执 method: expected=' + pending.method + ', actual=' + method);
      }
      if (!responseType || !envelope.raw || !envelope.raw.length) {
        this.log('回执不匹配: response body，expectedType=' + (responseType || '(none)')
          + ', rawBytes=' + (envelope.raw && envelope.raw.length || 0));
        this.onCommandResponse(envelope, null);
        return;
      }
      {
        // 球端将未签名的业务响应直接放在 Envelope.raw 字段中。
        const responseBytes = envelope.raw;
        const responseHex = Array.from(responseBytes).map(byte => byte.toString(16).padStart(2, '0')).join(' ');
        let responseText = '';
        try {
          responseText = new TextDecoder().decode(responseBytes).replace(/[\x00-\x1f]/g, '.');
        } catch (error) {
          responseText = '(not utf-8 text)';
        }
        this.log('回执业务 payload: length=' + responseBytes.length
          + ', hex=' + responseHex
          + ', text=' + responseText.slice(0, 240));
        try {
          response = decode(responseType, responseBytes);
        } catch (error) {
          this.logError('上行响应不是预期 Protobuf: ' + responseType, error);
          this.onCommandResponse(envelope, null);
          return;
        }
        const requestId = response && response.header && response.header.request_header
          && response.header.request_header.request_id;
        if (pending && requestId !== pending.requestId) {
          this.log('回执不匹配: request_id，expected=' + expectedRequestId + ', actual=' + (requestId || '(empty)'));
          this.onCommandResponse(envelope, response);
          return;
        }
        const matched = requestId ? this.pendingCommands.get(requestId) : null;
        if (matched) {
          clearTimeout(matched.timer);
          this.pendingCommands.delete(requestId);
          const rawStatusCode = response.header && response.header.status && response.header.status.code;
          const statusCode = Number(rawStatusCode);
          const statusOk = statusCode === 1 || rawStatusCode === 'CODE_OK';
          if (!statusOk) {
            const statusMessage = response.header && response.header.status && response.header.status.message;
            matched.reject(new Error((statusMessage || 'command 执行失败') + ': ' + (rawStatusCode || 'UNKNOWN')));
          } else {
            matched.resolve({ envelope, response });
            this.log('command 回执匹配成功: method=' + method + ', request_id=' + requestId);
          }
        }
      }
      this.onCommandResponse(envelope, response);
    } catch (error) {
      this.logError('解析 command 上行失败', error);
      this.onCommandResponse(data);
    }
  }

  // 释放连接建立 Promise 的成功回调。
  resolvePendingConnection() {
    if (!this.connectResolve) return;
    const resolve = this.connectResolve;
    this.connectResolve = null;
    this.connectReject = null;
    resolve();
  }

  // 释放连接建立 Promise 的失败回调。
  rejectPendingConnection(error) {
    if (!this.connectReject) return;
    const reject = this.connectReject;
    this.connectResolve = null;
    this.connectReject = null;
    reject(error);
  }

  // 让所有未完成的 command 请求结束，避免悬挂 Promise。
  rejectPendingCommands(error) {
    this.pendingCommands.forEach((pending) => {
      clearTimeout(pending.timer);
      pending.reject(error);
    });
    this.pendingCommands.clear();
  }

  // 统一记录错误、通知界面并进入断开流程。
  failConnection(message, error, sessionId) {
    if (sessionId !== undefined && sessionId !== this.sessionId) return;
    if (this.isCleaningUp) return;
    this.logError(message, error);
    this.setState('error');
    this.onError(message);
    const rejection = error instanceof Error ? error : new Error(message);
    rejection.teleopReported = true;
    this.rejectPendingConnection(rejection);
    this.disconnect('error');
  }

  // 尽力发送停止命令后关闭整个遥操作会话。
  disconnect(finalState = 'idle', options = {}) {
    if (this.disconnectPromise) return this.disconnectPromise;
    this.sessionId++;
    this.rejectPendingConnection(new Error('连接已取消'));
    this.isCleaningUp = true;
    this.stopWatchdog();
    this.teleopEnabled = false;
    const shouldSendStop = options.sendStop !== false && !!this.cryptoPrivateKey && this.isChannelOpen('command');
    this.disconnectPromise = (async () => {
      if (shouldSendStop) {
        try {
          await this.sendCommand('teleop.set_enabled', 'SetTeleopEnabledRequest', {
            header: { sequence: this.sequence++, request_timestamp_ms: Date.now(), request_id: generateUUID() },
            enabled: false
          }, { awaitResponse: false });
        } catch (error) {
          this.logError('发送停止遥控命令失败，继续关闭连接', error);
        }
      }
      this.cleanupResources();
      this.setState(finalState);
    })().finally(() => {
      this.disconnectPromise = null;
    });
    return this.disconnectPromise;
  }

  // 关闭 DataChannel、PeerConnection、信令和加密密钥引用。
  cleanupResources() {
    this.teleopEnabled = false;
    this.stopWatchdog();
    this.rejectPendingCommands(new Error('连接已关闭'));
    // 先通知信令服务删除对应 client session，再关闭本地 RTC 通道。
    this.closeSignaling();
    Object.keys(this.channels).forEach((label) => {
      const channel = this.channels[label];
      if (channel) {
        try { channel.close(); } catch (e) { /* ignore */ }
      }
      this.channels[label] = null;
    });
    this.closePeerConnection();
    // CryptoKey 不可导出；断开后清掉引用，下一次连接必须重新取钥并导入。
    this.cryptoPrivateKey = null;
  }

  // 关闭遥操作 PeerConnection 及其通道引用。
  closePeerConnection() {
    this.clearOfferTimeout();
    if (this.pc) {
      try { this.pc.close(); } catch (e) { /* ignore */ }
      this.pc = null;
    }
    Object.keys(this.channels).forEach((label) => { this.channels[label] = null; });
  }

  // 清理 Offer 等待超时定时器。
  clearOfferTimeout() {
    if (this.offerTimeout) clearTimeout(this.offerTimeout);
    this.offerTimeout = null;
  }

  // 关闭信令 WebSocket 并重置其状态。
  closeSignaling() {
    const socket = this.signalingSocket;
    this.signalingSocket = null;
    if (socket && socket.readyState === 1) {
      try {
        socket.send(JSON.stringify({ id: this.robotCode + '_teleop', type: 'bye' }));
        this.log('信令 bye 已发送，请求移除遥操作会话');
      } catch (error) {
        this.logError('发送信令 bye 失败', error);
      }
    }
    if (socket) {
      try { socket.close(); } catch (e) { /* ignore */ }
    }
    this.signalingReady = false;
  }
}
